import { Component, inject } from '@angular/core';
import { EmbedApi } from '@tytapp/api';
import { LoggerService } from '@tytapp/common';
import { ContentBlock, ContentBlockType } from '@tytapp/content';
import { ContentBlockEditorComponent } from '@tytapp/content/content-block-editor-component';
import { ContentBlockViewComponent } from '@tytapp/content/content-block-view-component';
import { isClientSide } from '@tytapp/environment-utils';

const SAMPLE_TWEET = 'https://twitter.com/TheYoungTurks/status/1754918131607200162';

export interface TweetBlock extends ContentBlock {
    html_embed: string;
    isPreview?: boolean;
}

@Component({
    template: `<div class="embed" [innerHTML]="block.html_embed | trustHtml"></div>`
})
export class ViewComponent extends ContentBlockViewComponent<TweetBlock> {
    private embedApi = inject(EmbedApi);
    private logger = inject(LoggerService);

    async ngOnInit() {
        if (this.block.isPreview) {
            if (!this.block.html_embed) {
                try {
                    let response = await this.embedApi.getTweet(SAMPLE_TWEET).toPromise();
                    this.block.html_embed = response.html;
                } catch (e) {
                    this.logger.error(`Could not add tweet embed:`);
                    this.logger.error(e);
                }
            }
        }
        if (isClientSide()) {
            setTimeout(() => {
                if (!window['twttr'])
                    return;
                window['twttr'].widgets.load();
            }, 100);
        }
    }
}

@Component({
    template: `
        <ng-container *ngIf="block.html_embed">
            <div class="tweet" [innerHTML]="block.html_embed | trustHtml"></div>
        </ng-container>
        <ng-container *ngIf="!block.html_embed">
            <form (submit)="fetchTweet(block)">
            <mat-form-field appearance="outline">
                <mat-label>Enter Tweet URL</mat-label>
                <input name="tweetUrl" type="text" matInput [(ngModel)]="block.__tweetUrl"
                    placeholder="https://twitter.com/jack/status/0123456789" [disabled]="locked" />
            </mat-form-field>
            <button mat-button color="primary" [disabled]="!block.__tweetUrl" [disabled]="locked">
                <mat-icon>add</mat-icon>
                Add Tweet
            </button>
            </form>
        </ng-container>
    `,
    styles: [
        `
        mat-form-field {
            width: 100%;
        }
        `
    ]
})
export class EditorComponent extends ContentBlockEditorComponent<TweetBlock> {
    private embedApi = inject(EmbedApi);
    private logger = inject(LoggerService);

    ngOnInit() {
        if (isClientSide()) {
            setTimeout(() => {
                if (!window['twttr'])
                    return;
                window['twttr'].widgets.load();
            }, 100);
        }
    }

    async fetchTweet(block: TweetBlock) {
        if (!block['__tweetUrl'])
            return;

        try {
            let url = block['__tweetUrl'];
            let response = await this.embedApi.getTweet(url).toPromise();
            block.html_embed = response.html;
        } catch (e) {
            this.logger.error(`Could not add tweet embed:`);
            this.logger.error(e);
            alert(`Could not add tweet! Is it a valid Twitter URL?`);
        }

        if (isClientSide()) {
            setTimeout(() => {
                if (!window['twttr'])
                    return;
                window['twttr'].widgets.load();
            }, 100);
        }
    }
}

export const TWEET_BLOCK_TYPE: ContentBlockType<TweetBlock> = {
    id: 'tweet',
    label: 'Tweet',
    icon: 'title',
    preview: {
        isPreview: true,
        html_embed: ''
    },
    editorComponent: EditorComponent,
    viewComponent: ViewComponent,
};
